// @spec RSK-STR-014
// @spec RSK-STR-057
'use client';

import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Bot, ShieldCheck, Workflow } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';

import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RiskAppetiteCard, RiskScoreMatrixCard } from '@/features/settings/risk';
import {
  CreateRiskCustomFieldDialog,
  CreateRiskSubcategoryDialog,
  DeleteRiskCustomFieldDialog,
  DeleteRiskSubcategoryDialog,
  EditRiskCustomFieldDialog,
  EditRiskSubcategoryDialog,
  RiskCustomFieldList,
  RiskSubcategoryList,
} from '@/features/settings/risks';
import type { RiskCategory, RiskCustomField, RiskSubcategory } from '@/features/settings/risks';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';

type RiskSettingsTab = 'workflow' | 'fields' | 'governance' | 'integrations';

const searchSchema = z.object({
  tab: z
    .enum(['workflow', 'fields', 'governance', 'integrations'])
    .optional()
    .default('workflow')
    .catch('workflow'),
});

export const Route = createFileRoute('/_authenticated/settings/risk')({
  validateSearch: searchSchema,
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.riskSettings'),
  }),
  component: RiskSettingsPage,
});

/**
 * Organization risk settings page for managing risk configuration.
 *
 * Allows organization admins to:
 * - Configure the risk scoring matrix (likelihood/impact scales and level thresholds)
 * - Configure category-specific risk thresholds
 * - Set overall acceptable and escalation scores
 * - Define approval level requirements for risk acceptance
 */
function RiskSettingsPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const { isAdmin } = useOrgRole();

  const activeTab: RiskSettingsTab = search.tab;

  const [isCreateFieldOpen, setIsCreateFieldOpen] = useState(false);
  const [editingField, setEditingField] = useState<RiskCustomField | null>(null);
  const [deletingField, setDeletingField] = useState<RiskCustomField | null>(null);

  const [createCategory, setCreateCategory] = useState<RiskCategory | null>(null);
  const [editingSubcategory, setEditingSubcategory] = useState<RiskSubcategory | null>(null);
  const [deletingSubcategory, setDeletingSubcategory] = useState<RiskSubcategory | null>(null);

  return (
    <div className="p-8">
      <div className="mx-auto max-w-5xl space-y-8">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">{t('riskSettings.pageTitle')}</h1>
          <p className="mt-1 text-muted-foreground">
            {t('riskSettings.pageDescription')}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Badge variant={isAdmin ? 'default' : 'secondary'}>
              {isAdmin ? t('riskSettings.editable') : t('riskSettings.viewOnly')}
            </Badge>
            <Badge variant="outline">{t('riskSettings.riskRegisterLinked')}</Badge>
          </div>
        </header>

        <Tabs
          value={activeTab}
          onValueChange={(value) => {
            void navigate({
              search: (prev) => ({ ...prev, tab: value as RiskSettingsTab }),
              replace: true,
            });
          }}
          className="space-y-6"
        >
          <TabsList>
            <TabsTrigger value="workflow">{t('riskSettings.tabWorkflow')}</TabsTrigger>
            <TabsTrigger value="fields">{t('riskSettings.tabFields')}</TabsTrigger>
            <TabsTrigger value="governance">{t('riskSettings.tabGovernance')}</TabsTrigger>
            <TabsTrigger value="integrations">{t('riskSettings.tabIntegrations')}</TabsTrigger>
          </TabsList>

          <TabsContent value="workflow" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('riskSettings.workflowControlsTitle')}</CardTitle>
                <CardDescription>
                  {t('riskSettings.workflowControlsDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                <Button variant="outline" asChild>
                  <Link to="/risks">
                    <Workflow className="mr-2 h-4 w-4" />
                    {t('riskSettings.openRiskRegister')}
                  </Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/risks/ai-suggestions">
                    <Bot className="mr-2 h-4 w-4" />
                    {t('riskSettings.openAiLeon')}
                  </Link>
                </Button>
              </CardContent>
            </Card>
            <RiskScoreMatrixCard isAdmin={isAdmin} />
            <RiskAppetiteCard isAdmin={isAdmin} />
          </TabsContent>

          <TabsContent value="fields" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('riskSettings.linkedWithFormsTitle')}</CardTitle>
                <CardDescription>
                  {t('riskSettings.linkedWithFormsDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                <Button variant="outline" asChild>
                  <Link to="/risks">{t('riskSettings.openRiskRegister')}</Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/settings/configration" search={{ aspect: 'risk', section: 'attributes' }}>
                    {t('riskSettings.openCustomConfiguration')}
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <RiskCustomFieldList
              onCreateClick={() => {
                setIsCreateFieldOpen(true);
              }}
              onEditClick={(field) => {
                setEditingField(field);
              }}
              onDeleteClick={(field) => {
                setDeletingField(field);
              }}
              isAdmin={isAdmin}
            />

            <RiskSubcategoryList
              onCreateClick={(category) => {
                setCreateCategory(category);
              }}
              onEditClick={(subcategory) => {
                setEditingSubcategory(subcategory);
              }}
              onDeleteClick={(subcategory) => {
                setDeletingSubcategory(subcategory);
              }}
              isAdmin={isAdmin}
            />
          </TabsContent>

          <TabsContent value="governance" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('riskSettings.governanceTitle')}</CardTitle>
                <CardDescription>
                  {t('riskSettings.governanceDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {t('riskSettings.governanceContentDescription')}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" asChild>
                    <Link
                      to="/settings/configration"
                      search={{ aspect: 'risk', section: 'guidelines' }}
                    >
                      {t('riskSettings.openRiskGuidelines')}
                    </Link>
                  </Button>
                  <Button variant="outline" asChild>
                    <Link to="/risks/approvals">
                      <ShieldCheck className="mr-2 h-4 w-4" />
                      {t('riskSettings.openApprovals')}
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="integrations" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('riskSettings.integrationsTitle')}</CardTitle>
                <CardDescription>
                  {t('riskSettings.integrationsDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                <Button variant="outline" asChild>
                  <Link to="/assets">{t('riskSettings.openAssetInventory')}</Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/vulnerabilities">{t('riskSettings.openVulnerabilities')}</Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/risks/ai-suggestions">
                    <Bot className="mr-2 h-4 w-4" />
                    {t('riskSettings.aiLeonSuggestions')}
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <CreateRiskCustomFieldDialog open={isCreateFieldOpen} onOpenChange={setIsCreateFieldOpen} />
      <EditRiskCustomFieldDialog
        field={editingField}
        open={editingField !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditingField(null);
          }
        }}
      />
      <DeleteRiskCustomFieldDialog
        field={deletingField}
        open={deletingField !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeletingField(null);
          }
        }}
      />

      <CreateRiskSubcategoryDialog
        category={createCategory}
        open={createCategory !== null}
        onOpenChange={(open) => {
          if (!open) {
            setCreateCategory(null);
          }
        }}
      />
      <EditRiskSubcategoryDialog
        subcategory={editingSubcategory}
        open={editingSubcategory !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditingSubcategory(null);
          }
        }}
      />
      <DeleteRiskSubcategoryDialog
        subcategory={deletingSubcategory}
        open={deletingSubcategory !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeletingSubcategory(null);
          }
        }}
      />
    </div>
  );
}
